export const PIXELS_PER_FOOT = 20;

export function ftToPx(ft: number): number {
  return ft * PIXELS_PER_FOOT;
}

export function pxToFt(px: number): number {
  return px / PIXELS_PER_FOOT;
}

export function snapToGrid(value: number, gridSize: number): number {
  return Math.round(value / gridSize) * gridSize;
}

export const CHAIR_SIZE_FT = 1.5;

export function calcCeremonyPositions(
  preset: 'grid' | 'curved' | 'vshape',
  rows: number,
  chairsPerRow: number,
  chairSpacingFt: number,
  rowSpacingFt: number,
  withAisle: boolean,
  aisleWidthFt: number,
): { positions: { x: number; y: number }[]; widthFt: number; heightFt: number } {
  const cs = CHAIR_SIZE_FT;
  const cstep = cs + chairSpacingFt;
  const rstep = cs + rowSpacingFt;
  const positions: { x: number; y: number }[] = [];

  if (preset === 'curved') {
    const spreadAngle = Math.PI * 0.75;
    for (let r = 0; r < rows; r++) {
      const radius = (chairsPerRow * cstep) / spreadAngle + r * rstep;
      const cx = (chairsPerRow * cstep) / 2;
      const cy = radius;
      for (let c = 0; c < chairsPerRow; c++) {
        const a = Math.PI - spreadAngle / 2 + (spreadAngle / Math.max(chairsPerRow - 1, 1)) * c;
        positions.push({ x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) });
      }
    }
    const minX = Math.min(...positions.map((p) => p.x));
    const minY = Math.min(...positions.map((p) => p.y));
    const norm = positions.map((p) => ({ x: p.x - minX, y: p.y - minY }));
    const maxX = Math.max(...norm.map((p) => p.x));
    const maxY = Math.max(...norm.map((p) => p.y));
    return { positions: norm, widthFt: maxX + cs, heightFt: maxY + cs };
  }

  if (preset === 'vshape') {
    const half = Math.floor(chairsPerRow / 2);
    const other = chairsPerRow - half;
    const gap = withAisle ? aisleWidthFt : cstep;
    for (let r = 0; r < rows; r++) {
      const inset = r * 1.0;
      for (let c = 0; c < half; c++) positions.push({ x: inset + c * cstep, y: r * rstep });
      const rightStart = half * cstep + gap;
      for (let c = 0; c < other; c++)
        positions.push({ x: rightStart + c * cstep - inset, y: r * rstep });
    }
  } else {
    // grid
    const half = Math.floor(chairsPerRow / 2);
    const other = chairsPerRow - half;
    for (let r = 0; r < rows; r++) {
      if (withAisle) {
        for (let c = 0; c < half; c++) positions.push({ x: c * cstep, y: r * rstep });
        const rightStart = half * cstep + aisleWidthFt;
        for (let c = 0; c < other; c++) positions.push({ x: rightStart + c * cstep, y: r * rstep });
      } else {
        for (let c = 0; c < chairsPerRow; c++) positions.push({ x: c * cstep, y: r * rstep });
      }
    }
  }

  const maxX = positions.length > 0 ? Math.max(...positions.map((p) => p.x)) : 0;
  const maxY = positions.length > 0 ? Math.max(...positions.map((p) => p.y)) : 0;
  return { positions, widthFt: maxX + cs, heightFt: maxY + cs };
}

// ─── Canvas orientation (display-only rotation / flip) ─────────
//
// Rotation and flip never touch object coordinates — they are applied as a
// single wrapper <g transform> over the canvas content. Both the producer
// canvas and the client-portal canvas derive their orientation from here.

export type CanvasRotation = 0 | 90 | 180 | 270;

export interface CanvasOrientation {
  /** viewBox width after rotation (width/height swap at 90/270). */
  viewW: number;
  /** viewBox height after rotation. */
  viewH: number;
  /** Wrapper <g> transform for the canvas content, or undefined when identity. */
  groupTransform: string | undefined;
}

export function rotateCanvas(rotation: CanvasRotation, direction: 1 | -1): CanvasRotation {
  return ((((rotation + direction * 90) % 360) + 360) % 360) as CanvasRotation;
}

export function getCanvasOrientation(
  widthPx: number,
  heightPx: number,
  rotation: CanvasRotation,
  flipH = false,
): CanvasOrientation {
  const swapped = rotation === 90 || rotation === 270;
  const viewW = swapped ? heightPx : widthPx;
  const viewH = swapped ? widthPx : heightPx;
  const parts: string[] = [];
  // Flip is applied in view space (outermost) so it mirrors what is on screen,
  // regardless of the current rotation.
  if (flipH) parts.push(`translate(${viewW}, 0) scale(-1, 1)`);
  if (rotation === 90) parts.push(`translate(${heightPx}, 0) rotate(90)`);
  else if (rotation === 180) parts.push(`translate(${widthPx}, ${heightPx}) rotate(180)`);
  else if (rotation === 270) parts.push(`translate(0, ${widthPx}) rotate(270)`);
  return { viewW, viewH, groupTransform: parts.length > 0 ? parts.join(' ') : undefined };
}

// ─── Rotation-aware bounding box ───────────────────────────────
//
// Items are stored and drawn as `translate(x, y) rotate(rotation, w/2, h/2)`:
// `x`/`y` are the top-left of the UN-rotated box, and the shape spins about
// its own center. So whenever width ≠ height and the rotation is not a
// multiple of 180°, the *visible* left/top edge no longer sits at `x`/`y` —
// e.g. a 12ft × 3ft table rotated 90° looks shifted ~4.5ft right of its
// stored `x`. These helpers convert between the stored anchor and the visible
// bounding box so the properties panel matches what the user sees on canvas.
// All values share the caller's unit (pixels at the current call sites).

/** Width/height of the axis-aligned bounding box of a rotated rectangle. */
export function rotatedAabbSize(
  width: number,
  height: number,
  rotationDeg: number,
): { width: number; height: number } {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  return {
    width: width * cos + height * sin,
    height: width * sin + height * cos,
  };
}

/** Stored anchor (un-rotated top-left) → visible top-left of the rotated box. */
export function anchorToVisualTopLeft(
  x: number,
  y: number,
  width: number,
  height: number,
  rotationDeg: number,
): { x: number; y: number } {
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  const box = rotatedAabbSize(width, height, rotationDeg);
  return { x: centerX - box.width / 2, y: centerY - box.height / 2 };
}

/** Visible top-left of the rotated box → stored anchor (un-rotated top-left). */
export function visualTopLeftToAnchor(
  visualX: number,
  visualY: number,
  width: number,
  height: number,
  rotationDeg: number,
): { x: number; y: number } {
  const box = rotatedAabbSize(width, height, rotationDeg);
  const centerX = visualX + box.width / 2;
  const centerY = visualY + box.height / 2;
  return { x: centerX - width / 2, y: centerY - height / 2 };
}

/**
 * Smallest stored anchor (per axis) that keeps the item's *visible* box flush
 * against the top/left walls — i.e. the anchor whose visible top-left is (0, 0).
 * For an un-rotated or 180° item this is {0, 0}; for a 12ft × 3ft table rotated
 * 90° it is {x: -4.5ft, y: +4.5ft} (in px), because the un-rotated anchor sits
 * off the visible box. Positions must be floored here rather than at 0 so a
 * rotated non-square item can actually reach the wall.
 */
export function visualAnchorFloor(
  width: number,
  height: number,
  rotationDeg: number,
): { x: number; y: number } {
  return visualTopLeftToAnchor(0, 0, width, height, rotationDeg);
}

export function getTableColor(
  seated: number,
  capacity: number,
): { fill: string; stroke: string; label: string } {
  if (seated === 0) return { fill: '#f3f4f6', stroke: '#9ca3af', label: 'Empty' };
  if (seated >= capacity) return { fill: '#dcfce7', stroke: '#16a34a', label: 'Full' };
  return { fill: '#fef9c3', stroke: '#ca8a04', label: 'Partial' };
}

// ─── Conversion: Backend FloorPlanObject ↔ CanvasItem ──────────
//
// Tables and non-table objects all live in `floor_plan_objects` now. An
// object is treated as a seatable table when its `tableNumber` is set.

import {
  calculateRoundTableChairs,
  calculateRectTableChairs,
  type BulkUpsertObjectsInput,
  type ChairSides,
  type FloorPlanObject,
} from '@peles/shared';
import type { CanvasItem } from './types';
import { TABLE_TYPE_REVERSE, isTableItem } from './types';

// ─── Item identity ──────────────────────────────────────────────
//
// Every canvas item carries a `uid`: a value that is unique within the plan
// and never reused. Links between items (a chair pointing at its table, a
// group of items that move together) are keyed off it. They used to be keyed
// off `tableNumber` / `sortOrder`, both of which repeat — a renumbered table
// or an object added after a deletion could take a number another item had
// already handed out, and the two silently became one item as far as moving,
// chair placement and deletion were concerned.

let uidCounter = 0;

export function newUid(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  uidCounter += 1;
  return `u${Date.now().toString(36)}-${uidCounter.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Group id for a parent and the chairs it owns (auto-created, not user-made). */
export function newChairGroupId(): string {
  return `chairs-${newUid()}`;
}

/** Group id for an explicit user grouping ("Group" in the properties panel). */
export function newUserGroupId(): string {
  return `group-${newUid()}`;
}

/** True for the auto group that ties a parent to its own chairs. */
export function isChairGroupId(groupId: string | undefined): boolean {
  return !!groupId?.startsWith('chairs-');
}

/**
 * Config for a copy of `item`: everything except the keys that make the copy
 * a duplicate identity rather than a new item — its uid, its group membership
 * and (for a copied chair) its link back to the original's parent.
 */
export function copyItemConfig(config: Record<string, unknown>): Record<string, unknown> {
  const clean = { ...config };
  delete clean.uid;
  delete clean.groupId;
  delete clean.parentUid;
  delete clean.parentTableNumber;
  delete clean.parentObjectSort;
  return clean;
}

export function fromFloorPlanObject(o: FloorPlanObject): CanvasItem {
  const config = (o.config as Record<string, unknown>) ?? {};
  // Prefer the top-level column; fall back to a legacy config.tableNumber for
  // rows that pre-date the unification migration and haven't been backfilled
  // yet (e.g. local dev DBs that haven't run the latest migration).
  const tableNumber =
    typeof o.tableNumber === 'number'
      ? o.tableNumber
      : typeof config.tableNumber === 'number'
        ? (config.tableNumber as number)
        : undefined;
  // Strip tableNumber out of the canvas-side config so we never round-trip
  // it back to the JSON column on save.
  // `uid` and `groupId` live in the JSON column but are item fields on the
  // canvas side — they must be removed from the config we keep, or a stale
  // copy round-trips back on save and resurrects a group the user removed.
  const cleanConfig: Record<string, unknown> = { ...config };
  delete cleanConfig.tableNumber;
  delete cleanConfig.uid;
  delete cleanConfig.groupId;
  return {
    id: o.id,
    uid: typeof config.uid === 'string' && config.uid ? config.uid : o.id || newUid(),
    objectType: o.objectType,
    label: o.label || '',
    x: o.x,
    y: o.y,
    widthFt: o.widthFt,
    heightFt: o.heightFt,
    rotation: o.rotation,
    color: o.color,
    opacity: o.opacity,
    seats: o.seats ?? 0,
    config: cleanConfig,
    sortOrder: o.sortOrder,
    ...(typeof tableNumber === 'number' ? { tableNumber } : {}),
    // groupId is still persisted inside config (read from the raw config —
    // cleanConfig no longer carries it).
    ...(typeof config.groupId === 'string' ? { groupId: config.groupId } : {}),
  };
}

/**
 * Convert canvas items to the bulk-upsert payload. Folds the UI-only
 * `groupId` into `config` so it round-trips through the JSON column.
 * Pass `stripIds` when saving into a different floor plan (e.g. Save As) —
 * the DB ids belong to the source plan's objects.
 */
export function itemsToObjectsPayload(
  items: CanvasItem[],
  opts?: { stripIds?: boolean },
): BulkUpsertObjectsInput['objects'] {
  return items.map((it) => {
    const config: Record<string, unknown> = { ...it.config, uid: it.uid };
    // Absent groupId must be written as absent, not left to a leftover key.
    if (it.groupId) config.groupId = it.groupId;
    else delete config.groupId;
    const o = toFloorPlanObject({ ...it, config });
    return {
      ...o,
      id: opts?.stripIds ? undefined : o.id,
      label: o.label ?? undefined,
      seats: o.seats ?? undefined,
      tableNumber: o.tableNumber ?? undefined,
      config: o.config ?? undefined,
    };
  });
}

// ─── Chair attachment ───────────────────────────────────────────
//
// Placed chairs link to their parent through config.parentUid. Every
// operation that moves, deletes, or re-seats a parent must carry its chairs
// along, so the linkage logic lives here rather than being re-derived at each
// call site. Legacy rows link by table number / sort order instead and are
// converted once, on load, by migrateLoadedItems.

export const CHAIR_COLOR = '#9ca3af';

export const CHAIR_PARENT_FIELD = 'parentUid';

export function chairParentRef(parent: CanvasItem): {
  field: typeof CHAIR_PARENT_FIELD;
  key: string;
} {
  return { field: CHAIR_PARENT_FIELD, key: parent.uid };
}

export function isChairOf(chair: CanvasItem, parent: CanvasItem): boolean {
  if (chair.objectType !== 'CHAIR' || parent.objectType === 'CHAIR') return false;
  if (!parent.uid) return false;
  return chair.config?.[CHAIR_PARENT_FIELD] === parent.uid;
}

/** The chairs attached to `parent`, in item order. */
export function chairsOf(items: CanvasItem[], parent: CanvasItem): CanvasItem[] {
  return items.filter((it) => isChairOf(it, parent));
}

export function canParentChairs(item: CanvasItem): boolean {
  return item.objectType !== 'CHAIR' && (isTableItem(item) || !!item.config?.canHaveChairs);
}

/** Indexes of all chairs attached to any of the given parents. */
export function attachedChairIndexes(items: CanvasItem[], parents: CanvasItem[]): Set<number> {
  const result = new Set<number>();
  const nonChairParents = parents.filter((p) => p && p.objectType !== 'CHAIR');
  if (nonChairParents.length === 0) return result;
  items.forEach((it, i) => {
    if (nonChairParents.some((p) => isChairOf(it, p))) result.add(i);
  });
  return result;
}

/** Whether chairs should be laid out radially (round/oval outline). */
export function isRoundParent(item: CanvasItem): boolean {
  const tableType = isTableItem(item)
    ? TABLE_TYPE_REVERSE[item.objectType] || item.objectType.replace('TABLE_', '')
    : null;
  const shape = (item.config?.shape as string) || '';
  return tableType === 'ROUND' || tableType === 'OVAL' || shape === 'CIRCLE' || shape === 'OVAL';
}

/**
 * Build the CHAIR items for a parent at its current position/rotation.
 * Chair centers are rotated around the table center so chairs track the
 * parent's rotation.
 */
export function buildChairsForParent(
  parent: CanvasItem,
  opts: {
    chairSizeFt: number;
    chairSides: ChairSides;
    sortBase: number;
    groupId: string;
    seats?: number;
  },
): CanvasItem[] {
  const seats = opts.seats ?? parent.seats;
  const { field, key } = chairParentRef(parent);
  const round = isRoundParent(parent);
  const positions = round
    ? calculateRoundTableChairs(parent.widthFt, seats, opts.chairSizeFt)
    : calculateRectTableChairs(
        parent.widthFt,
        parent.heightFt,
        seats,
        opts.chairSizeFt,
        opts.chairSides,
      );

  const centerX = parent.x + ftToPx(parent.widthFt) / 2;
  const centerY = parent.y + ftToPx(parent.heightFt) / 2;
  const rotRad = (parent.rotation * Math.PI) / 180;
  const cSzPx = ftToPx(opts.chairSizeFt);
  const halfTable = { x: ftToPx(parent.widthFt) / 2, y: ftToPx(parent.heightFt) / 2 };

  return positions.map((pos, i) => {
    const chairCenterX = round ? ftToPx(pos.x) : ftToPx(pos.x) + cSzPx / 2 - halfTable.x;
    const chairCenterY = round ? ftToPx(pos.y) : ftToPx(pos.y) + cSzPx / 2 - halfTable.y;
    const rx = chairCenterX * Math.cos(rotRad) - chairCenterY * Math.sin(rotRad);
    const ry = chairCenterX * Math.sin(rotRad) + chairCenterY * Math.cos(rotRad);
    return {
      objectType: 'CHAIR',
      label: `S${i + 1}`,
      x: centerX + rx - cSzPx / 2,
      y: centerY + ry - cSzPx / 2,
      widthFt: opts.chairSizeFt,
      heightFt: opts.chairSizeFt,
      rotation: Math.round(pos.rotation + parent.rotation) % 360,
      color: CHAIR_COLOR,
      opacity: 1,
      seats: 1,
      uid: newUid(),
      config: { [field]: key },
      sortOrder: opts.sortBase + i,
      groupId: opts.groupId,
    };
  });
}

/**
 * Remove and rebuild the chairs of the parents at the given indexes, using
 * each parent's CURRENT seats/size/position. Parents that have no chairs are
 * skipped unless `placeIfMissing`. Chair size defaults to the parent's
 * existing chair size so a seats-only change keeps the configuration.
 */
export function replaceChairsForParents(
  items: CanvasItem[],
  parentIdxs: number[],
  opts: {
    chairSides: ChairSides;
    defaultChairSizeFt: number;
    chairSizeFt?: number;
    placeIfMissing?: boolean;
  },
): CanvasItem[] {
  const parents = parentIdxs
    .map((i) => items[i])
    .filter((p): p is CanvasItem => !!p && canParentChairs(p));
  if (parents.length === 0) return items;

  const plans: { parent: CanvasItem; chairSizeFt: number; groupId: string }[] = [];
  const chairsToRemove = new Set<CanvasItem>();
  parents.forEach((parent) => {
    const existing = items.filter((it) => isChairOf(it, parent));
    if (existing.length === 0 && !opts.placeIfMissing) return;
    existing.forEach((c) => chairsToRemove.add(c));
    plans.push({
      parent,
      chairSizeFt: opts.chairSizeFt ?? existing[0]?.widthFt ?? opts.defaultChairSizeFt,
      groupId: parent.groupId || newChairGroupId(),
    });
  });
  if (plans.length === 0) return items;

  let result = items
    .filter((it) => !chairsToRemove.has(it))
    .map((it) => {
      const plan = plans.find((p) => p.parent === it);
      if (!plan) return it;
      const updated = it.groupId !== plan.groupId ? { ...it, groupId: plan.groupId } : it;
      plan.parent = updated;
      return updated;
    });
  plans.forEach(({ parent, chairSizeFt, groupId }) => {
    result = [
      ...result,
      ...buildChairsForParent(parent, {
        chairSizeFt,
        chairSides: opts.chairSides,
        sortBase: result.length,
        groupId,
      }),
    ];
  });
  return result;
}

/**
 * Apply per-item position deltas, extending each delta to the item's
 * attached chairs (unless the chair has its own delta already). Each axis is
 * clamped so the item's *visible* bounding box cannot cross the top/left wall
 * (`visualAnchorFloor`) — for un-rotated/square items this is the usual floor
 * at 0, but a rotated non-square item can now reach the wall (its stored anchor
 * goes negative). Only axes with a non-zero delta are clamped, so editing one
 * coordinate never nudges the other.
 */
export function moveItemsWithChairs(
  items: CanvasItem[],
  deltas: Map<number, { dx: number; dy: number }>,
): CanvasItem[] {
  const extended = new Map(deltas);
  deltas.forEach((d, idx) => {
    const parent = items[idx];
    if (!parent || parent.objectType === 'CHAIR') return;
    items.forEach((it, i) => {
      if (!extended.has(i) && isChairOf(it, parent)) extended.set(i, d);
    });
  });
  return items.map((it, i) => {
    const d = extended.get(i);
    if (!d || (d.dx === 0 && d.dy === 0)) return it;
    const floor = visualAnchorFloor(ftToPx(it.widthFt), ftToPx(it.heightFt), it.rotation);
    return {
      ...it,
      x: d.dx !== 0 ? Math.max(floor.x, it.x + d.dx) : it.x,
      y: d.dy !== 0 ? Math.max(floor.y, it.y + d.dy) : it.y,
    };
  });
}

/**
 * Expand a seed selection to everything that moves with it: whole groups of
 * any member (transitively) and the attached chairs of any non-chair member.
 */
export function expandMoveSet(items: CanvasItem[], seed: Iterable<number>): Set<number> {
  const set = new Set(seed);
  // New entries added during forEach are visited too, so group expansion is
  // transitive.
  set.forEach((mi) => {
    const item = items[mi];
    if (item?.groupId) {
      items.forEach((it, i) => {
        if (it.groupId === item.groupId) set.add(i);
      });
    }
  });
  const parents = [...set].map((i) => items[i]).filter((it): it is CanvasItem => !!it);
  attachedChairIndexes(items, parents).forEach((i) => set.add(i));
  return set;
}

/**
 * Normalise the items of a freshly loaded plan.
 *
 * Older rows link a chair to its parent by table number or by sort order, and
 * older auto-groups were named after those same numbers
 * (`chairs-parentTableNumber-5-restored`) — so any two tables that ever wore
 * number 5, or any two objects that ended up with the same sort order, were
 * read back as one group with one shared set of chairs. This runs once per
 * plan load and rewrites both onto uids:
 *
 *  - every legacy chair link is resolved to a single parent (the closest one
 *    when the old key is ambiguous) and stored as `config.parentUid`;
 *  - every merged auto chair-group is rebuilt with a fresh, unique id
 *    covering one parent and its own chairs, which un-merges plans already
 *    corrupted in production. Groups the user made by hand (`group-…`), and
 *    auto groups that are already intact, are left alone.
 *
 * It reports whether it changed anything, so the caller can persist a repair
 * once rather than re-deriving it on every load.
 */
export function migrateLoadedItems(items: CanvasItem[]): {
  items: CanvasItem[];
  changed: boolean;
} {
  // Whether anything was actually repaired. A plan that is already healthy
  // must come back untouched, so opening one never looks like an edit — and a
  // plan that was repaired is marked dirty by the caller so the repair reaches
  // the database instead of being re-derived (differently, once items have
  // moved) on every future load.
  let changed = false;

  // 1 — every item gets a uid that is unique inside the plan.
  const takenUids = new Set<string>();
  const withUids = items.map((it) => {
    const uid = it.uid && !takenUids.has(it.uid) ? it.uid : newUid();
    takenUids.add(uid);
    if (uid === it.uid) return it;
    changed = true;
    return { ...it, uid };
  });

  const centerOf = (it: CanvasItem) => ({
    x: it.x + ftToPx(it.widthFt) / 2,
    y: it.y + ftToPx(it.heightFt) / 2,
  });
  const nearest = (candidates: CanvasItem[], chair: CanvasItem): string | undefined => {
    if (candidates.length === 0) return undefined;
    if (candidates.length === 1) return candidates[0].uid;
    const c = centerOf(chair);
    let best = candidates[0];
    let bestDist = Infinity;
    candidates.forEach((cand) => {
      const cc = centerOf(cand);
      const d = (cc.x - c.x) ** 2 + (cc.y - c.y) ** 2;
      if (d < bestDist) {
        bestDist = d;
        best = cand;
      }
    });
    return best.uid;
  };

  // 2 — resolve chair → parent links onto uids, dropping the legacy keys.
  const resolved = withUids.map((it) => {
    if (it.objectType !== 'CHAIR') return it;
    const cfg = it.config ?? {};
    const linked =
      typeof cfg[CHAIR_PARENT_FIELD] === 'string' &&
      withUids.some((cand) => cand.objectType !== 'CHAIR' && cand.uid === cfg[CHAIR_PARENT_FIELD]);
    let parentUid = linked ? (cfg[CHAIR_PARENT_FIELD] as string) : undefined;
    if (!parentUid && typeof cfg.parentTableNumber === 'number') {
      parentUid = nearest(
        withUids.filter((cand) => isTableItem(cand) && cand.tableNumber === cfg.parentTableNumber),
        it,
      );
    }
    if (!parentUid && typeof cfg.parentObjectSort === 'number') {
      parentUid = nearest(
        withUids.filter(
          (cand) =>
            cand.objectType !== 'CHAIR' &&
            !isTableItem(cand) &&
            cand.sortOrder === cfg.parentObjectSort,
        ),
        it,
      );
    }
    const config = { ...cfg };
    delete config.parentTableNumber;
    delete config.parentObjectSort;
    if (parentUid) config[CHAIR_PARENT_FIELD] = parentUid;
    else delete config[CHAIR_PARENT_FIELD];
    if (
      'parentTableNumber' in cfg ||
      'parentObjectSort' in cfg ||
      config[CHAIR_PARENT_FIELD] !== cfg[CHAIR_PARENT_FIELD]
    ) {
      changed = true;
      return { ...it, config };
    }
    return it;
  });

  // 3 — rebuild the auto chair-groups, one fresh id per parent.
  const chairIdxByParent = new Map<string, number[]>();
  resolved.forEach((it, i) => {
    if (it.objectType !== 'CHAIR') return;
    const parentUid = it.config?.[CHAIR_PARENT_FIELD];
    if (typeof parentUid !== 'string') return;
    const list = chairIdxByParent.get(parentUid);
    if (list) list.push(i);
    else chairIdxByParent.set(parentUid, [i]);
  });

  const result = [...resolved];
  resolved.forEach((parent, i) => {
    if (parent.objectType === 'CHAIR') return;
    const chairIdxs = chairIdxByParent.get(parent.uid) ?? [];
    if (chairIdxs.length === 0) {
      if (isChairGroupId(parent.groupId)) {
        result[i] = { ...parent, groupId: undefined };
        changed = true;
      }
      return;
    }
    // A hand-made group the parent belongs to still wins — its chairs join it.
    // An auto group is kept when it already covers exactly this parent and its
    // own chairs; only a merged (or missing) one is re-minted, so a healthy
    // plan is left byte-identical.
    const current = parent.groupId;
    const owned = new Set([i, ...chairIdxs]);
    const intact =
      !!current &&
      (!isChairGroupId(current) ||
        resolved.every((it, oi) => (it.groupId === current) === owned.has(oi)));
    const gid = intact ? (current as string) : newChairGroupId();
    if (result[i].groupId !== gid) {
      result[i] = { ...result[i], groupId: gid };
      changed = true;
    }
    chairIdxs.forEach((ci) => {
      if (result[ci].groupId !== gid) {
        result[ci] = { ...result[ci], groupId: gid };
        changed = true;
      }
    });
  });
  // Chairs left without a parent must not drag a stale group along.
  result.forEach((it, i) => {
    if (
      it.objectType === 'CHAIR' &&
      typeof it.config?.[CHAIR_PARENT_FIELD] !== 'string' &&
      isChairGroupId(it.groupId)
    ) {
      result[i] = { ...it, groupId: undefined };
      changed = true;
    }
  });
  return { items: result, changed };
}

export function toFloorPlanObject(
  item: CanvasItem,
): Omit<FloorPlanObject, 'id' | 'floorPlanId' | 'createdAt' | 'updatedAt'> & { id?: string } {
  return {
    id: item.id,
    objectType: item.objectType as FloorPlanObject['objectType'],
    label: item.label || null,
    x: item.x,
    y: item.y,
    widthFt: item.widthFt,
    heightFt: item.heightFt,
    rotation: item.rotation,
    color: item.color,
    opacity: item.opacity,
    seats: item.seats ?? null,
    tableNumber: typeof item.tableNumber === 'number' ? item.tableNumber : null,
    config: Object.keys(item.config).length > 0 ? item.config : null,
    sortOrder: item.sortOrder,
  };
}
